// @flow
import { LOCATION_CHANGE } from "connected-react-router";
import { startRequest, finishRequest } from "@store/utils";

import {
  GetAbsenceReasonTrendsDefaultReports,
  GetAbsenceReasonTrendsDefaultReportsSuccess,
  GetAbsenceReasonTrendsDefaultReportsError,
  GetAbsenceReasonTrendsMyReports,
  GetAbsenceReasonTrendsMyReportsSuccess,
  GetAbsenceReasonTrendsMyReportsError,
  GetAbsenceReasonTrendsReportData,
  GetAbsenceReasonTrendsReportDataSuccess,
  GetAbsenceReasonTrendsReportDataError,
  GetAbsenceProfile,
  GetAbsenceProfileSuccess,
  GetAbsenceProfileError,
  ClearAbsenceProfile,
  GetAbsenceReasonTrendsReportSchemaSuccess,
  GetAbsenceReasonTrendsReportSchemaError,
  SetAbsenceReasonTrendsReportToSave,
  SetAbsenceReasonTrendsReportViewState,
  ExportAbsenceReasonTrendsReport,
  ExportAbsenceReasonTrendsReportSuccess,
  ExportAbsenceReasonTrendsReportError,
} from "@modules/reporting/constants/actions";
import type { AbsenceReasonState, AbsenceReasonReportViewState } from "./types";
import { cloneObject } from "@utils/object";

export const initialViewState: AbsenceReasonReportViewState = {
  filtersExpanded: false,
  chartTop5ReasonsExpanded: true,
  chartTop5ReasonsTrendExpanded: true,
  reportTableExpanded: true,
  absenceProfileExpanded: true,
  selectedAbsenceReason: "",
};

export const initialState: AbsenceReasonState = {
  error: null,
  requestsInProgress: 0,
  reportGenerateInProgress: 0,
  defaultReports: [],
  myReports: [],
  report: {
    details: null,
    reportData: {
      details: [],
      top5ReasonsByPeriod: [],
      top5Reasons: [],
    },
  },
  viewState: initialViewState,
  absenceProfile: null,
  schema: null,
  reportToSave: null,
};

export const setLoading = (state: AbsenceReasonState) => () => startRequest(state);

export const onError = (state: AbsenceReasonState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onGetAbsenceReasonTrendsDefaultReportsSuccess = (state: AbsenceReasonState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    defaultReports: payload,
  };
};
export const onGetAbsenceReasonTrendsMyReportsSuccess = (state: AbsenceReasonState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    myReports: payload,
  };
};

export const onGetAbsenceReasonTrendsReportDataSuccess = (state: AbsenceReasonState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    report: payload,
  };
};

export const onGetAbsenceProfileSuccess = (state: AbsenceReasonState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    absenceProfile: payload,
  };
};

export const onClearAbsenceProfile = (state: AbsenceReasonState) => ({ payload }: Object) => {
  return {
    ...state,
    absenceProfile: null,
  };
};

export const onGetAbsenceReasonTrendsReportSchemaSuccess = (state: AbsenceReasonState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    schema: payload,
  };
};

export const onGetAbsenceReasonTrendsReportSchemaError = (state: AbsenceReasonState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    error: payload,
  };
};

export const onSetAbsenceReasonTrendsReportToSave = (state: AbsenceReasonState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    reportToSave: payload,
  };
};

export const onSetViewState = (state: AbsenceReasonState) => ({ payload }: Object) => {
  return {
    ...state,
    viewState: payload,
  };
};

export const onResetViewState = (state: AbsenceReasonState) => () => ({
  ...state,
  viewState: initialViewState,
});

export const onExportAbsenceReasonTrendsReport = (state: AbsenceReasonState) => () => {
  return {
    ...state,
    reportGenerateInProgress: ++state.reportGenerateInProgress,
  };
};

export const onExportAbsenceReasonTrendsReportLoaded = (state: AbsenceReasonState) => () => {
  return {
    ...state,
    reportGenerateInProgress:
      state.reportGenerateInProgress > 0 ? --state.reportGenerateInProgress : 0,
  };
};

export default (state: AbsenceReasonState = initialState, action: Object): AbsenceReasonState => {
  const act = {
    [ExportAbsenceReasonTrendsReport]: onExportAbsenceReasonTrendsReport,
    [ExportAbsenceReasonTrendsReportSuccess]: onExportAbsenceReasonTrendsReportLoaded,
    [ExportAbsenceReasonTrendsReportError]: onExportAbsenceReasonTrendsReportLoaded,
    [GetAbsenceReasonTrendsDefaultReports]: setLoading,
    [GetAbsenceReasonTrendsDefaultReportsSuccess]: onGetAbsenceReasonTrendsDefaultReportsSuccess,
    [GetAbsenceReasonTrendsDefaultReportsError]: onError,
    [GetAbsenceReasonTrendsMyReports]: setLoading,
    [GetAbsenceReasonTrendsMyReportsSuccess]: onGetAbsenceReasonTrendsMyReportsSuccess,
    [GetAbsenceReasonTrendsMyReportsError]: onError,
    [GetAbsenceReasonTrendsReportData]: setLoading,
    [GetAbsenceReasonTrendsReportDataSuccess]: onGetAbsenceReasonTrendsReportDataSuccess,
    [GetAbsenceReasonTrendsReportDataError]: onError,
    [GetAbsenceProfile]: setLoading,
    [GetAbsenceProfileSuccess]: onGetAbsenceProfileSuccess,
    [GetAbsenceProfileError]: onError,
    [ClearAbsenceProfile]: onClearAbsenceProfile,
    [GetAbsenceReasonTrendsReportSchemaSuccess]: onGetAbsenceReasonTrendsReportSchemaSuccess,
    [GetAbsenceReasonTrendsReportSchemaError]: onGetAbsenceReasonTrendsReportSchemaError,
    [SetAbsenceReasonTrendsReportToSave]: onSetAbsenceReasonTrendsReportToSave,
    [SetAbsenceReasonTrendsReportViewState]: onSetViewState,
    [LOCATION_CHANGE]: onResetViewState,
  };

  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
